import { useMemo, useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";

// Closed = completed in this app. Canceled work is still archived
// (separate filter) but isn't the default.
const RECORD_KINDS = [
  { id: "completed", label: "Completed Work Orders" },
  { id: "canceled", label: "Cancelled Work Orders" },
];

export default function FacilityWorkRecordsPage({
  jobsRaw = [],
  facilityServiceTicketsRaw = [],
  customers = [],
  onSelectJob,
  onBack,
}) {
  const [kind, setKind] = useState("completed");
  const [search, setSearch] = useState("");

  const buildingByJob = useMemo(() => {
    const m = new Map();
    for (const c of customers) {
      for (const b of c.locations || []) {
        for (const j of jobsRaw) {
          if (j.locationId === b.id) {
            m.set(j.id, { building: b, customer: c });
          }
        }
      }
    }
    return m;
  }, [customers, jobsRaw]);

  const ticketCount = (jobId) =>
    facilityServiceTicketsRaw.filter((t) => t.jobId === jobId).length;

  const filtered = useMemo(() => {
    const targetStatus = kind === "canceled" ? "canceled" : "closed";
    const q = search.trim().toLowerCase();
    return jobsRaw
      .filter((j) => j.status === targetStatus)
      .filter((j) => {
        if (!q) return true;
        const ctx = buildingByJob.get(j.id);
        const hay = [
          j.jobNumber,
          j.title,
          j.description,
          ctx?.building?.name,
          ctx?.customer?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort(
        (a, b) =>
          new Date(b.closedAt || b.updatedAt || 0) -
          new Date(a.closedAt || a.updatedAt || 0)
      );
  }, [jobsRaw, kind, search, buildingByJob]);

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString() : "—";

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Work Records</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        Archive of completed work — the work order plus every service
        ticket the maintenance team filed against it. Open one to see its
        tickets and history.
      </p>

      <div
        style={{
          ...styles.card,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontSize: "12px", color: "#666" }}>Kind:</label>
        {RECORD_KINDS.map((k) => (
          <button
            key={k.id}
            style={{
              ...styles.button,
              margin: 0,
              background: kind === k.id ? "#1a5fb4" : "#f3f4f6",
              color: kind === k.id ? "white" : "#222",
              fontWeight: kind === k.id ? 600 : 500,
            }}
            onClick={() => setKind(k.id)}
          >
            {k.label}
          </button>
        ))}
        <input
          style={{ ...styles.input, margin: 0, flex: 1, minWidth: "200px" }}
          placeholder="Search by job #, title, building, customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <p style={{ fontSize: "13px", color: "#666", margin: "8px 0" }}>
        {filtered.length} record(s)
      </p>

      {filtered.length === 0 ? (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          No records match these filters.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {filtered.map((j) => {
            const ctx = buildingByJob.get(j.id);
            const tCount = ticketCount(j.id);
            return (
              <li
                key={j.id}
                style={{ ...styles.card, cursor: "pointer" }}
                onClick={() => onSelectJob && onSelectJob(j)}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <strong>
                    {j.jobNumber ? `#${j.jobNumber} — ` : ""}
                    {j.title}
                  </strong>
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "1px 6px",
                      background:
                        kind === "canceled" ? "#f8d7da" : "#c9efc9",
                      color: kind === "canceled" ? "#c12c2c" : "#1a7f37",
                      borderRadius: "4px",
                      fontWeight: 600,
                    }}
                  >
                    {kind === "canceled" ? "Cancelled" : "Completed"}
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginLeft: "auto",
                    }}
                  >
                    {tCount} ticket{tCount === 1 ? "" : "s"}
                  </span>
                </div>
                <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                  {ctx?.customer?.name || "—"}
                  {ctx?.building?.name ? ` · ${ctx.building.name}` : ""}
                  {j.closedAt && ` · closed ${fmtDate(j.closedAt)}`}
                </div>
                {j.description && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#444",
                      marginTop: "4px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {j.description}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
